import { Box } from '@mui/material';
import { TabPanel } from 'devextreme-react';
import OverlayProvider from 'contextProviders/OverlayContext';
import React, { useState, useCallback, useEffect } from 'react';
import { SnackbarProvider } from 'notistack';
import { PricelistTabs } from '../../../../../enums/PricelistTabs';
import { IMasterPricelistData } from '../../../../../interfaces/Pricelist/IMasterPricelistData';
import AnalysisInfo from '../../MasterDetailPanel/components/AnalysisInfo/AnalysisInfo';
import AnalyteInfo from '../../MasterDetailPanel/components/AnalyteInfo/AnalyteInfo';
import DiscountTable from '../../MasterDetailPanel/components/DiscountTable/DiscountTable';
import GroupAnalysis from '../../MasterDetailPanel/components/GroupAnalysis/GroupAnalysis';
import MediaInfo from '../../MasterDetailPanel/components/MediaInfo/MediaInfo';
import OtherInfo from '../../MasterDetailPanel/components/OtherInfo/OtherInfo';
import PrepAnalysis from '../../MasterDetailPanel/components/PrepAnalysis/PrepAnalysis';
import SubAnalysis from '../../MasterDetailPanel/components/SubAnalysis/SubAnalysis';

const MasterPricelistDetailView = (detailData: IMasterPricelistData) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [availableTabs, setAvailableTabs] = useState([]);

  const renderTab = useCallback(
    (tab) => {
      switch (tab) {
        case PricelistTabs.DiscountTable:
          return <DiscountTable analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} price={detailData.Price} costPrice={detailData.CostPrice} isFixedPrice={detailData.IsFixedPrice} />;
        case PricelistTabs.AnalysisInfo:
          return <AnalysisInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.AnalyteInfo:
          return <AnalyteInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.GroupAnalysis:
          return <GroupAnalysis analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.PrepAnalysis:
          return <PrepAnalysis analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.SubAnalysis:
          return <SubAnalysis analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.MediaInfo:
          return <MediaInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.OtherInfo:
          return <OtherInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        default:
          return null;
      }
    },
    [detailData]
  );

  const tabSelectionChanged = useCallback(
    (e) => {
      setSelectedTab(availableTabs.indexOf(e.addedItems[0].title));
    },
    [availableTabs]
  );

  useEffect(() => {
    const tabs = [];

    if(!detailData.Analysis){
      return;
    }

    tabs.push(PricelistTabs.DiscountTable);

    if (detailData.IsTracking) {
      tabs.push(PricelistTabs.AnalysisInfo);

      if (detailData.IsPrepFee) {
        tabs.push(PricelistTabs.PrepAnalysis);
      }
    } else if (detailData.IsStandardGroup) {
      tabs.push(PricelistTabs.SubAnalysis);
      tabs.push(PricelistTabs.GroupAnalysis);
    } else {
      tabs.push(PricelistTabs.AnalyteInfo);
      tabs.push(PricelistTabs.AnalysisInfo);
    }

    if (detailData.HasMedia) {
      tabs.push(PricelistTabs.MediaInfo);
    }

    tabs.push(PricelistTabs.OtherInfo);

    setAvailableTabs(tabs);
  }, [detailData]);

  return (
    <Box className="masterDetailView">
      <TabPanel
        swipeEnabled={false}
        selectedIndex={selectedTab}
        onSelectionChanged={tabSelectionChanged}
        itemComponent={({ data }) => <OverlayProvider><SnackbarProvider maxSnack={3}>{renderTab(data.title)}</SnackbarProvider></OverlayProvider>}
        dataSource={availableTabs.map((m) => ({ title: m }))}
      />
    </Box>
  );
};

export default React.memo(MasterPricelistDetailView);
